import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const SessionSetupModal = ({ isOpen, onClose, onStartSession, availableQuestions }) => {
  const [sessionConfig, setSessionConfig] = useState({
    questionCount: 20,
    timedMode: false,
    timeLimit: 30,
    showExplanations: true,
    randomOrder: true
  });

  const handleStartSession = () => {
    onStartSession(sessionConfig);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-xl shadow-elevation-4 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Settings" size={20} className="text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Session Setup</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Database" size={16} className="text-primary" />
              <span className="text-xs font-medium text-primary">Available Questions</span>
            </div>
            <p className="text-2xl font-bold text-primary data-text">
              {availableQuestions?.toLocaleString()}
            </p>
          </div>

          <Input
            label="Number of Questions"
            type="number"
            value={sessionConfig?.questionCount}
            onChange={(e) => setSessionConfig({
              ...sessionConfig,
              questionCount: Math.min(Math.max(1, parseInt(e?.target?.value) || 1), availableQuestions)
            })}
            min={1}
            max={availableQuestions}
            description={`Choose between 1 and ${availableQuestions?.toLocaleString()} questions`}
          />

          <div className="space-y-3">
            <Checkbox
              label="Timed Practice Mode"
              description="Set a time limit for your practice session"
              checked={sessionConfig?.timedMode}
              onChange={(e) => setSessionConfig({
                ...sessionConfig,
                timedMode: e?.target?.checked
              })}
            />

            {sessionConfig?.timedMode && (
              <Input
                label="Time Limit (minutes)"
                type="number"
                value={sessionConfig?.timeLimit}
                onChange={(e) => setSessionConfig({
                  ...sessionConfig,
                  timeLimit: Math.max(1, parseInt(e?.target?.value) || 1)
                })}
                min={1}
                description="Recommended: 1.2 minutes per question"
                className="ml-8"
              />
            )}
          </div>

          <Checkbox
            label="Show Explanations Immediately"
            description="Display answer explanations after each question"
            checked={sessionConfig?.showExplanations}
            onChange={(e) => setSessionConfig({
              ...sessionConfig,
              showExplanations: e?.target?.checked
            })}
          />

          <Checkbox
            label="Randomize Question Order"
            description="Present questions in random sequence"
            checked={sessionConfig?.randomOrder}
            onChange={(e) => setSessionConfig({
              ...sessionConfig,
              randomOrder: e?.target?.checked
            })}
          />

          <div className="pt-4 border-t border-border">
            <div className="bg-muted/50 rounded-lg p-4 mb-4">
              <h3 className="text-sm font-semibold text-foreground mb-2">Session Summary</h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Icon name="FileText" size={14} />
                  <span>{sessionConfig?.questionCount} questions</span>
                </li>
                {sessionConfig?.timedMode && (
                  <li className="flex items-center gap-2">
                    <Icon name="Clock" size={14} />
                    <span>{sessionConfig?.timeLimit} minutes total</span>
                  </li>
                )}
                <li className="flex items-center gap-2">
                  <Icon name="Lightbulb" size={14} />
                  <span>Explanations: {sessionConfig?.showExplanations ? 'Immediate' : 'End of session'}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Shuffle" size={14} />
                  <span>Order: {sessionConfig?.randomOrder ? 'Random' : 'Sequential'}</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                fullWidth
                onClick={handleStartSession}
                iconName="Play"
                iconPosition="left"
              >
                Start Practice
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionSetupModal;